import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import type { HttpRouteRule, HttpTargetSystem } from '../types';
import {
  listAllRouteRules,
  createRouteRule,
  updateRouteRule,
  deleteRouteRule,
  listTargetSystems,
} from '../api/http-route-api';

type FormMode = 'create' | 'edit';

interface RuleFormData {
  rule_name: string;
  method: string;
  source_path: string;
  target_system_code: string;
  target_path: string;
  match_condition_json: string;
  priority: number;
  response_mode: string;
  timeout_ms: number;
  enabled: number;
  remark: string;
}

const EMPTY_FORM: RuleFormData = {
  rule_name: '',
  method: 'GET',
  source_path: '',
  target_system_code: '',
  target_path: '',
  match_condition_json: '{}',
  priority: 100,
  response_mode: 'standardize',
  timeout_ms: 10000,
  enabled: 1,
  remark: '',
};

const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'] as const;
const RESPONSE_MODE_OPTIONS = ['standardize', 'passthrough'] as const;

@customElement('http-route-rule-view')
export class HttpRouteRuleView extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
        sans-serif;
    }

    .page-wrap {
      max-width: 1400px;
      margin: 0 auto;
    }

    .toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 1.25rem;
      flex-wrap: wrap;
    }

    .toolbar h2 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: #0f172a;
    }

    .rule-count {
      margin-left: 0.5rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #64748b;
    }

    .btn-primary {
      border: none;
      background: #2684ff;
      color: white;
      font-size: 0.8125rem;
      font-weight: 600;
      padding: 0.5rem 0.9rem;
      border-radius: 6px;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.15s;
    }

    .btn-primary:hover:not(:disabled) {
      background: #005cc5;
    }

    .btn-primary:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    /* Table */
    .table-wrap {
      background: white;
      border-radius: 8px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      border: 1px solid #e1e4e8;
      overflow-x: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8125rem;
    }

    thead {
      background: #f8fafc;
      border-bottom: 2px solid #e1e4e8;
    }

    th {
      text-align: left;
      padding: 0.65rem 0.75rem;
      font-weight: 600;
      color: #475569;
      text-transform: uppercase;
      font-size: 0.6875rem;
      letter-spacing: 0.03em;
      white-space: nowrap;
    }

    td {
      padding: 0.6rem 0.75rem;
      border-bottom: 1px solid #f0f4f8;
      color: #334e68;
      vertical-align: middle;
    }

    tbody tr:hover {
      background: #f8fafc;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    .badge {
      display: inline-block;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
      font-size: 0.6875rem;
      font-weight: 600;
    }

    .badge-on {
      background: #dcfce7;
      color: #166534;
    }

    .badge-off {
      background: #fee2e2;
      color: #991b1b;
    }

    .method-badge {
      display: inline-block;
      padding: 0.15rem 0.45rem;
      border-radius: 3px;
      font-size: 0.6875rem;
      font-weight: 700;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    }

    .method-GET { background: #dbeafe; color: #1e40af; }
    .method-POST { background: #dcfce7; color: #166534; }
    .method-PUT { background: #fef3c7; color: #92400e; }
    .method-DELETE { background: #fee2e2; color: #991b1b; }
    .method-PATCH { background: #ede9fe; color: #5b21b6; }
    .method-HEAD { background: #e0e7ff; color: #3730a3; }
    .method-OPTIONS { background: #fce7f3; color: #9d174d; }

    .actions-cell {
      display: flex;
      gap: 0.35rem;
      white-space: nowrap;
    }

    .btn-sm {
      border: 1px solid #d9e2ec;
      background: white;
      color: #334e68;
      font-size: 0.6875rem;
      font-weight: 500;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      cursor: pointer;
      transition: border-color 0.15s, color 0.15s;
    }

    .btn-sm:hover:not(:disabled) {
      border-color: #2684ff;
      color: #2684ff;
    }

    .btn-sm.danger:hover:not(:disabled) {
      border-color: #dc2626;
      color: #dc2626;
    }

    .btn-sm:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .empty-state {
      text-align: center;
      padding: 3rem 1rem;
      color: #829ab1;
      font-size: 0.875rem;
    }

    .loading-state {
      text-align: center;
      padding: 2rem 1rem;
      color: #627d98;
      font-size: 0.875rem;
    }

    .hint-bar {
      margin-top: 0.75rem;
      font-size: 0.8125rem;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;
    }

    .hint-info {
      background: #f0f7ff;
      color: #1e40af;
    }

    .hint-err {
      background: #fef2f2;
      color: #991b1b;
    }

    /* Modal */
    .overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(15, 23, 42, 0.4);
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 1.5rem 1rem;
      box-sizing: border-box;
      overflow-y: auto;
      z-index: 1000;
      animation: fadeIn 0.2s ease;
    }

    .modal {
      background: white;
      width: 100%;
      max-width: 620px;
      max-height: min(90vh, 900px);
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      animation: slideUp 0.3s ease;
      margin: auto;
    }

    .modal-header {
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;
    }

    .modal-title {
      font-size: 1.125rem;
      font-weight: 600;
      color: #0f172a;
      margin: 0;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 1.25rem;
      color: #64748b;
      cursor: pointer;
      padding: 0;
      line-height: 1;
    }

    .close-btn:hover {
      color: #0f172a;
    }

    .modal-body {
      padding: 1.5rem;
      overflow-y: auto;
      flex: 1;
      min-height: 0;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }

    @media (max-width: 520px) {
      .form-row {
        grid-template-columns: 1fr;
      }
    }

    .form-group {
      margin-bottom: 1rem;
    }

    .form-group:last-child {
      margin-bottom: 0;
    }

    .form-group label {
      display: block;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #334e68;
      margin-bottom: 0.35rem;
    }

    .form-group input,
    .form-group select,
    .form-group textarea {
      width: 100%;
      box-sizing: border-box;
      padding: 0.5rem 0.65rem;
      border-radius: 6px;
      border: 1px solid #bcccdc;
      font-size: 0.875rem;
      outline: none;
      transition: border-color 0.2s;
      background: white;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group select:focus,
    .form-group textarea:focus {
      border-color: #2684ff;
    }

    .form-group textarea {
      min-height: 80px;
      resize: vertical;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.8125rem;
      line-height: 1.45;
    }

    .form-group textarea.json-area {
      min-height: 120px;
    }

    .form-group input[type='number'] {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    }

    .form-hint {
      font-size: 0.6875rem;
      color: #829ab1;
      margin-top: 0.25rem;
    }

    .modal-footer {
      padding: 1rem 1.5rem;
      background: #f8fafc;
      border-top: 1px solid #e2e8f0;
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      flex-shrink: 0;
      position: sticky;
      bottom: 0;
      z-index: 1;
    }

    .btn {
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      border: none;
      transition: all 0.2s;
    }

    .btn-cancel {
      background: white;
      color: #475569;
      border: 1px solid #cbd5e1;
    }

    .btn-cancel:hover:not(:disabled) {
      background: #f1f5f9;
    }

    .btn-submit {
      background: #2684ff;
      color: white;
    }

    .btn-submit:hover:not(:disabled) {
      background: #005cc5;
    }

    .btn-submit:disabled,
    .btn-cancel:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .code-inline {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.75rem;
      background: #f0f4f8;
      padding: 0.1rem 0.35rem;
      border-radius: 3px;
      color: #334e68;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `;

  @state() private rules: HttpRouteRule[] = [];
  @state() private targetSystems: HttpTargetSystem[] = [];
  @state() private loading = false;
  @state() private hint = '';
  @state() private hintKind: 'info' | 'err' = 'info';

  // Modal state
  @state() private showModal = false;
  @state() private formMode: FormMode = 'create';
  @state() private editingId: number | null = null;
  @state() private form: RuleFormData = { ...EMPTY_FORM };
  @state() private saving = false;

  protected firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);
    void this.fetchData();
  }

  private async fetchData(): Promise<void> {
    this.loading = true;
    this.hint = '';
    try {
      const [rulesRes, systemsRes] = await Promise.all([
        listAllRouteRules(),
        listTargetSystems(),
      ]);
      this.rules = Array.isArray(rulesRes) ? rulesRes : [];
      this.targetSystems = Array.isArray(systemsRes) ? systemsRes : [];
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.setHint(`加载数据失败: ${msg}`, 'err');
    } finally {
      this.loading = false;
    }
  }

  private setHint(msg: string, kind: 'info' | 'err' = 'info'): void {
    this.hint = msg;
    this.hintKind = kind;
  }

  private openCreateModal(): void {
    this.formMode = 'create';
    this.editingId = null;
    this.form = { ...EMPTY_FORM };
    this.hint = '';
    this.showModal = true;
  }

  private openEditModal(rule: HttpRouteRule): void {
    this.formMode = 'edit';
    this.editingId = rule.id;
    this.form = {
      rule_name: rule.rule_name,
      method: rule.method,
      source_path: rule.source_path,
      target_system_code: rule.target_system_code,
      target_path: rule.target_path,
      match_condition_json: rule.match_condition_json || '{}',
      priority: rule.priority,
      response_mode: rule.response_mode,
      timeout_ms: rule.timeout_ms,
      enabled: rule.enabled,
      remark: rule.remark || '',
    };
    this.hint = '';
    this.showModal = true;
  }

  private closeModal(): void {
    this.showModal = false;
    this.form = { ...EMPTY_FORM };
    this.editingId = null;
  }

  private updateFormField(field: keyof RuleFormData, value: string | number): void {
    this.form = { ...this.form, [field]: value };
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (!this.form.rule_name.trim()) {
      this.setHint('规则名称不能为空', 'err');
      return;
    }
    if (!this.form.source_path.trim()) {
      this.setHint('源路径不能为空', 'err');
      return;
    }

    this.saving = true;
    this.hint = '';
    const enabled = Number(this.form.enabled);
    console.log('[http-route-rule] submit enabled raw=', this.form.enabled, 'parsed=', enabled);
    try {
      const payload = {
        rule_name: this.form.rule_name.trim(),
        method: this.form.method,
        source_path: this.form.source_path.trim(),
        target_system_code: this.form.target_system_code,
        target_path: this.form.target_path.trim(),
        match_condition_json: this.form.match_condition_json,
        priority: this.form.priority,
        response_mode: this.form.response_mode,
        timeout_ms: this.form.timeout_ms,
        enabled,
        remark: this.form.remark.trim(),
      };
      console.log('[http-route-rule] submit payload enabled=', payload.enabled, typeof payload.enabled);
      if (this.formMode === 'create') {
        await createRouteRule(payload);
        this.setHint(`路由规则 "${this.form.rule_name}" 创建成功`, 'info');
      } else if (this.editingId !== null) {
        await updateRouteRule(this.editingId, payload);
        this.setHint(`路由规则 "${this.form.rule_name}" 更新成功`, 'info');
      }
      this.closeModal();
      await this.fetchData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.setHint(`保存失败: ${msg}`, 'err');
    } finally {
      this.saving = false;
    }
  }

  private async handleDelete(rule: HttpRouteRule): Promise<void> {
    if (!confirm(`确定要删除路由规则 "${rule.rule_name}" (ID: ${rule.id}) 吗？此操作不可恢复。`)) {
      return;
    }
    try {
      await deleteRouteRule(rule.id);
      this.setHint(`路由规则 "${rule.rule_name}" 已删除`, 'info');
      await this.fetchData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.setHint(`删除失败: ${msg}`, 'err');
    }
  }

  private targetSystemDisplay(systemCode: string): string {
    if (!systemCode) return '—';
    const sys = this.targetSystems.find((s) => s.system_code === systemCode);
    return sys ? `${sys.system_name} (${systemCode})` : systemCode;
  }

  private formatTime(iso: string): string {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  private renderTable(): unknown {
    if (this.loading) {
      return html`<div class="loading-state">加载中...</div>`;
    }
    if (this.rules.length === 0) {
      return html`<div class="empty-state">暂无路由规则，点击「新建规则」创建第一条。</div>`;
    }

    // Sort by priority descending, then id ascending
    const sorted = [...this.rules].sort((a, b) => {
      if (a.priority !== b.priority) return b.priority - a.priority;
      return a.id - b.id;
    });

    return html`
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>规则名称</th>
            <th>方法</th>
            <th>源路径</th>
            <th>目标系统</th>
            <th>目标路径</th>
            <th>优先级</th>
            <th>状态</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          ${sorted.map(
            (rule) => html`
              <tr>
                <td><span class="code-inline">${rule.id}</span></td>
                <td>${rule.rule_name}</td>
                <td>
                  <span class="method-badge method-${rule.method}">${rule.method}</span>
                </td>
                <td><span class="code-inline">${rule.source_path}</span></td>
                <td>${this.targetSystemDisplay(rule.target_system_code)}</td>
                <td><span class="code-inline">${rule.target_path || '—'}</span></td>
                <td><span class="code-inline">${rule.priority}</span></td>
                <td>
                  <span class="badge ${rule.enabled === 1 ? 'badge-on' : 'badge-off'}">
                    ${rule.enabled === 1 ? '启用' : '禁用'}
                  </span>
                </td>
                <td>${this.formatTime(rule.created_at)}</td>
                <td>
                  <div class="actions-cell">
                    <button
                      type="button"
                      class="btn-sm"
                      @click=${() => this.openEditModal(rule)}
                    >
                      编辑
                    </button>
                    <button
                      type="button"
                      class="btn-sm danger"
                      @click=${() => this.handleDelete(rule)}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            `
          )}
        </tbody>
      </table>
    `;
  }

  private renderModal(): unknown {
    if (!this.showModal) return html``;

    const title = this.formMode === 'create' ? '新建路由规则' : `编辑路由规则: ${this.form.rule_name}`;

    return html`
      <div
        class="overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.closeModal();
        }}
      >
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">${title}</h3>
            <button class="close-btn" @click=${() => this.closeModal()}>&times;</button>
          </div>

          <form @submit=${this.handleSubmit}>
            <div class="modal-body">
              <div class="form-group">
                <label>规则名称 *</label>
                <input
                  type="text"
                  .value=${this.form.rule_name}
                  placeholder="例如：巡逻计划路由"
                  required
                  @input=${(e: Event) =>
                    this.updateFormField('rule_name', (e.target as HTMLInputElement).value)}
                />
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>HTTP 方法</label>
                  <select
                    @change=${(e: Event) =>
                      this.updateFormField('method', (e.target as HTMLSelectElement).value)}
                  >
                    ${METHOD_OPTIONS.map(
                      (m) => html`<option value=${m} ?selected=${this.form.method === m}>${m}</option>`
                    )}
                  </select>
                </div>

                <div class="form-group">
                  <label>优先级</label>
                  <input
                    type="number"
                    .value=${String(this.form.priority)}
                    min="0"
                    max="9999"
                    @input=${(e: Event) =>
                      this.updateFormField(
                        'priority',
                        Number((e.target as HTMLInputElement).value)
                      )}
                  />
                  <div class="form-hint">数字越大优先级越高</div>
                </div>
              </div>

              <div class="form-group">
                <label>源路径 *</label>
                <input
                  type="text"
                  .value=${this.form.source_path}
                  placeholder="/api/car-api/patrol-plan/info"
                  required
                  @input=${(e: Event) =>
                    this.updateFormField('source_path', (e.target as HTMLInputElement).value)}
                />
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>目标系统</label>
                  <select
                    @change=${(e: Event) =>
                      this.updateFormField(
                        'target_system_code',
                        (e.target as HTMLSelectElement).value
                      )}
                  >
                    <option value="" ?selected=${this.form.target_system_code === ''}>— 不指定 —</option>
                    ${this.targetSystems.map(
                      (sys) => html`
                        <option
                          value=${sys.system_code}
                          ?selected=${this.form.target_system_code === sys.system_code}
                        >
                          ${sys.system_name} (${sys.system_code})
                        </option>
                      `
                    )}
                  </select>
                  <div class="form-hint">从已配置的目标系统中选择</div>
                </div>

                <div class="form-group">
                  <label>目标路径</label>
                  <input
                    type="text"
                    .value=${this.form.target_path}
                    placeholder="/api/car-api/patrol-plan/info"
                    @input=${(e: Event) =>
                      this.updateFormField('target_path', (e.target as HTMLInputElement).value)}
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>响应模式</label>
                  <select
                    @change=${(e: Event) =>
                      this.updateFormField(
                        'response_mode',
                        (e.target as HTMLSelectElement).value
                      )}
                  >
                    ${RESPONSE_MODE_OPTIONS.map(
                      (m) => html`<option value=${m} ?selected=${this.form.response_mode === m}>${m}</option>`
                    )}
                  </select>
                  <div class="form-hint">standardize: 标准化包装; passthrough: 透传</div>
                </div>

                <div class="form-group">
                  <label>超时 (ms)</label>
                  <input
                    type="number"
                    .value=${String(this.form.timeout_ms)}
                    min="100"
                    max="600000"
                    step="100"
                    @input=${(e: Event) =>
                      this.updateFormField(
                        'timeout_ms',
                        Number((e.target as HTMLInputElement).value)
                      )}
                  />
                  <div class="form-hint">
                    转发超时以本字段为准（非目标系统读取超时）。文件上传建议 180000（3 分钟）。
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label>匹配条件 (JSON)</label>
                <textarea
                  class="json-area"
                  .value=${this.form.match_condition_json}
                  spellcheck="false"
                  placeholder='{}'
                  @input=${(e: Event) =>
                    this.updateFormField(
                      'match_condition_json',
                      (e.target as HTMLTextAreaElement).value
                    )}
                ></textarea>
                <div class="form-hint">
                  可选的 JSON 格式匹配条件，例如
                  {"query_params": {"type": "patrol"}} 或 {"headers": {"X-Version": "v2"}}
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>状态</label>
                  <select
                    @change=${(e: Event) =>
                      this.updateFormField(
                        'enabled',
                        Number((e.target as HTMLSelectElement).value)
                      )}
                  >
                    <option value="1" ?selected=${this.form.enabled === 1}>启用</option>
                    <option value="0" ?selected=${this.form.enabled === 0}>禁用</option>
                  </select>
                </div>

                <div class="form-group">
                  <label>备注</label>
                  <input
                    type="text"
                    .value=${this.form.remark}
                    placeholder="可选备注"
                    @input=${(e: Event) =>
                      this.updateFormField('remark', (e.target as HTMLInputElement).value)}
                  />
                </div>
              </div>

              ${this.hint
                ? html`<div class="hint-bar ${this.hintKind === 'err' ? 'hint-err' : 'hint-info'}">
                    ${this.hint}
                  </div>`
                : null}
            </div>

            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-cancel"
                @click=${() => this.closeModal()}
                ?disabled=${this.saving}
              >
                取消
              </button>
              <button type="submit" class="btn btn-submit" ?disabled=${this.saving}>
                ${this.saving ? '保存中...' : '保存'}
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  render(): unknown {
    return html`
      <div class="page-wrap">
        <div class="toolbar">
          <h2>路由规则配置 <span class="rule-count">共 ${this.rules.length} 条</span></h2>
          <button type="button" class="btn-primary" @click=${() => this.openCreateModal()}>
            + 新建规则
          </button>
        </div>

        ${this.hint && !this.showModal
          ? html`<div class="hint-bar ${this.hintKind === 'err' ? 'hint-err' : 'hint-info'}">
              ${this.hint}
            </div>`
          : null}

        <div class="table-wrap">${this.renderTable()}</div>

        ${this.renderModal()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'http-route-rule-view': HttpRouteRuleView;
  }
}
